import { Button, Checkbox, FormField, Notice, Select, Text, TextField } from "@smooth/design-system-components"
import dayjs from "dayjs"
import { useState } from "react"
import { useExecuteManualReconciliation } from "../hooks/useExecuteManualReconciliation"
import type { ManualReconciliationFormValues } from "../types"

const PAYMENT_KIND_OPTIONS = [
  { value: "bank_transfer", label: "銀行振り込み" },
  { value: "standing_order", label: "口座振替" },
]

const RECONCILIATION_MODE_OPTIONS = [
  { value: "default", label: "元本 + 遅延損害金の両方を消し込む" },
  { value: "principal_only", label: "元本のみを消し込む" },
  { value: "late_charge_only", label: "遅延損害金のみを消し込む" },
]

const initialValues = (): ManualReconciliationFormValues => ({
  userId: "",
  paymentKind: "bank_transfer",
  paymentAmount: "",
  whenPaymentAt: dayjs().format("YYYY-MM-DD"),
  bankTransferSummary: "",
  lateChargeAmount: "",
  reconciliationMode: "default",
  paymentPromiseId: "",
  disableRefund: false,
})

type Outcome = { kind: "success"; result: string } | { kind: "error"; message: string }

export const ManualReconciliation = () => {
  const { execute, isExecuting } = useExecuteManualReconciliation()
  const [values, setValues] = useState<ManualReconciliationFormValues>(initialValues)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const set = <K extends keyof ManualReconciliationFormValues>(key: K, value: ManualReconciliationFormValues[K]) =>
    setValues({ ...values, [key]: value })

  const handleSubmit = async () => {
    setOutcome(null)
    const response = await execute(values)
    if (response.ok === false) {
      setOutcome({ kind: "error", message: response.message })
      return
    }
    setOutcome({ kind: "success", result: response.result })
  }

  return (
    <div className="flex flex-col gap-4">
      {outcome && (
        <Notice tone={outcome.kind === "success" ? "info" : "error"} variant="card">
          <Text size="sm" as="span">
            {outcome.kind === "success" ? "消し込み結果" : "消し込みに失敗しました"}
          </Text>
          <pre className="whitespace-pre-wrap break-all text-caption">
            {outcome.kind === "success" ? outcome.result : outcome.message}
          </pre>
        </Notice>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          handleSubmit()
        }}
      >
        <FormField label="user_id" required>
          <TextField inputMode="numeric" value={values.userId} onChange={(e) => set("userId", e.target.value)} />
        </FormField>
        <FormField label="支払い種目" required>
          <Select
            options={PAYMENT_KIND_OPTIONS}
            value={values.paymentKind}
            onChange={(e) => set("paymentKind", e.target.value as ManualReconciliationFormValues["paymentKind"])}
          />
        </FormField>
        <FormField label="支払い金額" required>
          <TextField inputMode="numeric" value={values.paymentAmount} onChange={(e) => set("paymentAmount", e.target.value)} />
        </FormField>
        <FormField label="支払い日">
          <TextField type="date" value={values.whenPaymentAt} onChange={(e) => set("whenPaymentAt", e.target.value)} />
        </FormField>
        <FormField label="摘要（銀行振込時）" hint="空欄なら unknown として記録します">
          <TextField value={values.bankTransferSummary} onChange={(e) => set("bankTransferSummary", e.target.value)} />
        </FormField>
        <FormField label="遅延損害金">
          <TextField
            inputMode="numeric"
            value={values.lateChargeAmount}
            onChange={(e) => set("lateChargeAmount", e.target.value)}
          />
        </FormField>
        <FormField label="消し込み対象">
          <Select
            options={RECONCILIATION_MODE_OPTIONS}
            value={values.reconciliationMode}
            onChange={(e) =>
              set("reconciliationMode", e.target.value as ManualReconciliationFormValues["reconciliationMode"])
            }
          />
        </FormField>
        <FormField label="[任意]payment_promise_id" hint="※ 契約が複数存在する場合に使用して下さい">
          <TextField
            inputMode="numeric"
            value={values.paymentPromiseId}
            onChange={(e) => set("paymentPromiseId", e.target.value)}
          />
        </FormField>
        <Checkbox
          label="超過分も消し込む"
          checked={values.disableRefund}
          onChange={(e) => set("disableRefund", e.target.checked)}
        />
        <div>
          <Button type="submit" disabled={isExecuting}>
            {isExecuting ? "消し込み中..." : "消し込む"}
          </Button>
        </div>
      </form>
    </div>
  )
}
